import { useState } from "react";
import { useScrollRevealChildren } from "../../../hooks/useScrollReveal";

interface LeadFormSectionProps {
  variant?: "light" | "dark";
}

export default function LeadFormSection({ variant = "light" }: LeadFormSectionProps) {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useScrollRevealChildren();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    if (loading) return;
    setLoading(true);
    try {
      await fetch("https://readdy.ai/api/form/d71ug8ghb0rqicn68ocg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  const isDark = variant === "dark";
  const bg = isDark ? "bg-[#111010]" : "bg-white";
  const headingColor = isDark ? "text-white" : "text-[#1C1C1C]";
  const subColor = isDark ? "text-white/50" : "text-[#666]";
  const labelColor = isDark ? "text-white/50" : "text-[#666]";
  const inputClass = isDark
    ? "bg-white/5 border-white/10 text-white placeholder-white/25 focus:border-[#C9A961]"
    : "bg-[#F5F0E8] border-[#E8E2D9] text-[#1C1C1C] placeholder-[#BBB] focus:border-[#C9A961]";
  const cardBorder = isDark ? "border-white/8" : "border-[#E8E2D9]";

  return (
    <section className={`${bg} py-28`}>
      <div ref={ref} className="max-w-screen-xl mx-auto px-6 xl:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-start">

          {/* Left info panel */}
          <div>
            <div className="reveal flex items-center gap-3 mb-6">
              <div className="h-px w-10 bg-[#C9A961]" />
              <span
                className="text-[#C9A961] text-[11px] tracking-[0.25em] uppercase font-medium"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Start Your Project
              </span>
            </div>
            <h2
              className={`reveal delay-100 text-4xl md:text-5xl xl:text-[52px] leading-tight mb-6 ${headingColor}`}
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}
            >
              Ready to Transform Your Space?
            </h2>
            <p
              className={`reveal delay-200 text-base leading-relaxed mb-12 ${subColor}`}
              style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
            >
              Schedule your complimentary design consultation. Our team visits your home, assesses your space, and provides a detailed proposal — completely free, zero obligation.
            </p>

            <div className="space-y-6">
              {[
                { icon: "ri-phone-fill", label: "Call Us Directly", value: "(561) 441-6008", href: "tel:5614416008" },
                { icon: "ri-mail-line", label: "Email Us", value: "elliott@kitchenrp.com", href: "mailto:elliott@kitchenrp.com" },
                { icon: "ri-map-pin-line", label: "Visit Our Showroom", value: "5250 Town Center Cir #109, Boca Raton, FL 33486", href: "#" },
                { icon: "ri-time-line", label: "Showroom Hours", value: "Mon–Fri: 10:00 AM – 5:00 PM", href: "#" },
              ].map((item, idx) => (
                <div key={item.label} className={`reveal delay-${idx * 100 + 300} flex items-start gap-4`}>
                  <div className={`w-11 h-11 flex items-center justify-center flex-shrink-0 ${isDark ? "bg-[#C9A961]/12" : "bg-[#C9A961]/10"}`}>
                    <i className={`${item.icon} text-[#C9A961] text-base`} />
                  </div>
                  <div>
                    <p
                      className={`text-[10px] tracking-[0.18em] uppercase font-semibold mb-1 ${labelColor}`}
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {item.label}
                    </p>
                    {item.href !== "#" ? (
                      <a
                        href={item.href}
                        className={`text-sm hover:text-[#C9A961] transition-colors cursor-pointer ${headingColor}`}
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p className={`text-sm ${headingColor}`} style={{ fontFamily: "'Inter', sans-serif" }}>
                        {item.value}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right form */}
          <div className={`reveal-right p-10 md:p-12 border ${cardBorder}`}>
            {submitted ? (
              <div className="text-center py-14">
                <div className={`w-16 h-16 flex items-center justify-center ${isDark ? "bg-[#C9A961]/12" : "bg-[#C9A961]/10"} mx-auto mb-6`}>
                  <i className="ri-checkbox-circle-line text-[#C9A961] text-3xl" />
                </div>
                <h3
                  className={`text-2xl font-semibold mb-3 ${headingColor}`}
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Thank You!
                </h3>
                <p
                  className={`text-sm leading-relaxed ${subColor}`}
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  We&apos;ve received your request and will be in touch within 24 hours to schedule your complimentary consultation.
                </p>
              </div>
            ) : (
              <form
                data-readdy-form
                id="lead-contact-form"
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="mb-8">
                  <h3
                    className={`text-2xl font-semibold mb-1 ${headingColor}`}
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    Request Free Consultation
                  </h3>
                  <p className={`text-xs ${labelColor}`} style={{ fontFamily: "'Inter', sans-serif" }}>
                    No obligation · Response within 24 hours
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-[10px] tracking-[0.14em] uppercase font-semibold mb-2 ${labelColor}`} style={{ fontFamily: "'Inter', sans-serif" }}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="full_name"
                      required
                      placeholder="Your full name"
                      className={`w-full px-4 py-3 border text-sm outline-none transition-colors duration-200 ${inputClass}`}
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    />
                  </div>
                  <div>
                    <label className={`block text-[10px] tracking-[0.14em] uppercase font-semibold mb-2 ${labelColor}`} style={{ fontFamily: "'Inter', sans-serif" }}>
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="(555) 000-0000"
                      className={`w-full px-4 py-3 border text-sm outline-none transition-colors duration-200 ${inputClass}`}
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    />
                  </div>
                </div>

                <div>
                  <label className={`block text-[10px] tracking-[0.14em] uppercase font-semibold mb-2 ${labelColor}`} style={{ fontFamily: "'Inter', sans-serif" }}>
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="your@email.com"
                    className={`w-full px-4 py-3 border text-sm outline-none transition-colors duration-200 ${inputClass}`}
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  />
                </div>

                <div>
                  <label className={`block text-[10px] tracking-[0.14em] uppercase font-semibold mb-2 ${labelColor}`} style={{ fontFamily: "'Inter', sans-serif" }}>
                    Service Interested In
                  </label>
                  <select
                    name="service"
                    className={`w-full px-4 py-3 border text-sm outline-none transition-colors duration-200 cursor-pointer ${inputClass}`}
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    <option value="">Select a service...</option>
                    <option>Kitchen Remodeling</option>
                    <option>Bathroom Renovation</option>
                    <option>Custom Cabinetry</option>
                    <option>Outdoor Kitchen</option>
                    <option>Custom Closets</option>
                    <option>Premium Flooring</option>
                    <option>Full Home Renovation</option>
                  </select>
                </div>

                <div>
                  <label className={`block text-[10px] tracking-[0.14em] uppercase font-semibold mb-2 ${labelColor}`} style={{ fontFamily: "'Inter', sans-serif" }}>
                    Tell Us About Your Project
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    placeholder="Describe your project, timeline, and any specific requirements..."
                    className={`w-full px-4 py-3 border text-sm outline-none transition-colors duration-200 resize-none ${inputClass}`}
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#C9A961] text-white text-[11px] tracking-[0.18em] uppercase font-semibold py-4 hover:bg-[#b8964f] transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-70"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {loading ? "Sending..." : "Request Free Consultation"}
                </button>
                <p
                  className={`text-[10px] text-center ${labelColor}`}
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  No obligation · Complimentary consultation · Serving all of South Florida
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
